// src/lib/throttle.ts
/**
 * @fileoverview Faithful, dependency-free replacement for `lodash/throttle`.
 *
 * `lodash/throttle` was the only `lodash` import left in the client graph (used
 * by `ScrollTracker`, which ships in every page's layout bundle, and by
 * `WavyBackground`). Replacing it drops the lodash subtree from the client
 * bundle. This implementation matches the parts of lodash's contract that
 * matter for a drop-in:
 *
 * - **leading + trailing edges** (both default `true`, configurable). A single
 *   call within a window invokes only on the leading edge — the trailing edge
 *   fires only when at least one more call arrives during the wait (latest args
 *   win). This is lodash's behavior.
 * - **`this` binding** preserved via `Function.prototype.apply`.
 * - **`.cancel()`** clears a pending trailing invocation and resets the window.
 * - **`.flush()`** immediately runs a pending trailing invocation, if any.
 *
 * Intentionally omitted (unused here, and `throttle`-irrelevant): `maxWait`
 * (a debounce concept) and returning the last result (callers are void). The
 * `wait`/`now` math uses a `null` "never invoked" sentinel so the first call
 * fires on the leading edge regardless of the wall-clock value (a `0` sentinel
 * mis-fires when `Date.now()` is 0, e.g. under fake timers).
 */

export interface ThrottleOptions {
  /** Invoke on the leading edge of the wait window (default true). */
  leading?: boolean
  /** Invoke on the trailing edge of the wait window (default true). */
  trailing?: boolean
}

export interface Throttled<A extends unknown[]> {
  (...args: A): void
  /** Cancel any pending trailing invocation and reset the throttle window. */
  cancel: () => void
  /** Immediately invoke any pending trailing call now. */
  flush: () => void
}

/**
 * Create a throttled version of `fn` that runs at most once per `wait` ms.
 *
 * @param fn - Function to throttle.
 * @param wait - Minimum interval between invocations, in milliseconds.
 * @param options - `{ leading = true, trailing = true }`.
 */
export function throttle<A extends unknown[]>(
  fn: (...args: A) => void,
  wait: number,
  options: ThrottleOptions = {},
): Throttled<A> {
  const leading = options.leading ?? true
  const trailing = options.trailing ?? true

  // `null` = never invoked yet (first call fires the leading edge).
  let lastInvoke: number | null = null
  let timer: ReturnType<typeof setTimeout> | null = null
  let pendingArgs: A | null = null
  let pendingThis: unknown = undefined

  const invoke = (now: number, args: A, ctx: unknown): void => {
    lastInvoke = now
    pendingArgs = null
    pendingThis = null
    fn.apply(ctx, args)
  }

  const onTimerExpired = (): void => {
    timer = null
    // Trailing edge: fire only if a call arrived since the last invoke.
    if (trailing && pendingArgs) invoke(Date.now(), pendingArgs, pendingThis)
  }

  function throttled(this: unknown, ...args: A): void {
    const now = Date.now()
    pendingArgs = args
    // Capture the caller's `this` so a deferred trailing invoke binds it too.
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    pendingThis = this
    // Time left in the current window (0 on the first call, so it fires now).
    const remaining = lastInvoke === null ? 0 : wait - (now - lastInvoke)

    if (remaining <= 0 || remaining > wait) {
      // Leading edge / window elapsed / clock skew.
      if (timer) {
        clearTimeout(timer)
        timer = null
      }
      if (leading) {
        invoke(now, args, this)
      } else {
        // Leading suppressed: open the window now; the trailing edge invokes.
        lastInvoke = now
        if (trailing) timer = setTimeout(onTimerExpired, wait)
      }
      return
    }
    // Inside the window: schedule a single trailing invocation (latest args win).
    if (trailing) timer ??= setTimeout(onTimerExpired, remaining)
  }

  throttled.cancel = (): void => {
    if (timer) {
      clearTimeout(timer)
      timer = null
    }
    lastInvoke = null
    pendingArgs = null
    pendingThis = null
  }

  throttled.flush = (): void => {
    if (timer) {
      clearTimeout(timer)
      timer = null
      if (trailing && pendingArgs) invoke(Date.now(), pendingArgs, pendingThis)
    }
  }

  return throttled
}
